import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BusinessRoleView,
  BusinessRoleType,
  BusinessRoleStatus,
  CreateBusinessRoleInput,
  UpdateBusinessRoleInput,
  BusinessRoleDependencyInput,
  BusinessRoleSuccessCriteriaInput,
  createBusinessRole,
  updateBusinessRole,
} from '../../../api/General/businessRoles';
import { AssetCategoryView, getCategories } from '../../../api/General/assets';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter,
  DialogTitle,
} from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';

const roleTypeLabels: Record<BusinessRoleType, string> = {
  [BusinessRoleType.System]: 'System',
  [BusinessRoleType.Project]: 'Project',
  [BusinessRoleType.Temporary]: 'Temporary',
};

const statusLabels: Record<BusinessRoleStatus, string> = {
  [BusinessRoleStatus.Active]: 'Active',
  [BusinessRoleStatus.Inactive]: 'Inactive',
  [BusinessRoleStatus.Draft]: 'Draft',
};

interface Props {
  role: BusinessRoleView | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export default function RoleEditModal({ role, open, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = !!role;

  const [name, setName] = useState('');
  const [type, setType] = useState<BusinessRoleType>(BusinessRoleType.System);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<BusinessRoleStatus>(BusinessRoleStatus.Draft);
  const [numberOfSeats, setNumberOfSeats] = useState(1);
  const [coreResponsibilities, setCoreResponsibilities] = useState<string[]>([]);
  const [newResponsibility, setNewResponsibility] = useState('');
  const [decisionRights, setDecisionRights] = useState<string[]>([]);
  const [newDecisionRight, setNewDecisionRight] = useState('');
  const [dependencies, setDependencies] = useState<BusinessRoleDependencyInput[]>([]);
  const [successCriteria, setSuccessCriteria] = useState<BusinessRoleSuccessCriteriaInput>({});
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [requiredSkills, setRequiredSkills] = useState<string[]>([]);
  const [newSkill, setNewSkill] = useState('');
  const [neededEquipment, setNeededEquipment] = useState<string[]>([]);
  const [categories, setCategories] = useState<AssetCategoryView[]>([]);
  const [revisionComment, setRevisionComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getCategories().then((result) => {
      if (result.errors.length === 0) setCategories(result.payload ?? []);
    });
  }, []);

  useEffect(() => {
    if (open && role) {
      setName(role.name);
      setType(role.type);
      setDescription(role.description || '');
      setCategory(role.category || '');
      setStatus(role.status);
      setNumberOfSeats(role.numberOfSeats);
      setCoreResponsibilities([...role.coreResponsibilities]);
      setDecisionRights([...role.decisionRights]);
      setDependencies(role.dependencies.map(d => ({ role: d.role, description: d.description })));
      setSuccessCriteria(role.successCriteria || {});
      setTags([...role.tags]);
      setRequiredSkills([...role.requiredSkills]);
      setNeededEquipment(
        Array.isArray(role.neededEquipment)
          ? role.neededEquipment
          : (() => { try { return JSON.parse(role.neededEquipment ?? "[]"); } catch { return []; } })()
      );
      setRevisionComment('');
    } else if (open && !role) {
      setName('');
      setType(BusinessRoleType.System);
      setDescription('');
      setCategory('');
      setStatus(BusinessRoleStatus.Draft);
      setNumberOfSeats(1);
      setCoreResponsibilities([]);
      setDecisionRights([]);
      setDependencies([]);
      setSuccessCriteria({});
      setTags([]);
      setRequiredSkills([]);
      setNeededEquipment([]);
      setRevisionComment('');
    }
    setError('');
  }, [open, role]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError(t('businessRoles.errors.nameRequired'));
      return;
    }

    setSaving(true);
    setError('');

    try {
      const base: CreateBusinessRoleInput = {
        name: name.trim(),
        type,
        description: description || undefined,
        category: category || undefined,
        status,
        numberOfSeats,
        coreResponsibilities: coreResponsibilities.length > 0 ? coreResponsibilities : undefined,
        decisionRights: decisionRights.length > 0 ? decisionRights : undefined,
        dependencies: dependencies.length > 0 ? dependencies : undefined,
        successCriteria: successCriteria.description ? successCriteria : undefined,
        tags: tags.length > 0 ? tags : undefined,
        requiredSkills: requiredSkills.length > 0 ? requiredSkills : undefined,
        neededEquipment: neededEquipment.length > 0 ? neededEquipment : undefined,
      };

      let result;
      if (isEdit && role) {
        const input: UpdateBusinessRoleInput = { ...base, id: role.id, revisionComment: revisionComment || undefined };
        result = await updateBusinessRole(input);
      } else {
        result = await createBusinessRole(base);
      }

      if (result.errors.length === 0) {
        onSaved();
        onClose();
      } else {
        setError(result.errors[0] || t('businessRoles.errors.saveFailed'));
      }
    } catch {
      setError(t('businessRoles.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const addListItem = (list: string[], setList: (v: string[]) => void, value: string, setValue: (v: string) => void) => {
    if (value.trim() && !list.includes(value.trim())) {
      setList([...list, value.trim()]);
      setValue('');
    }
  };

  const removeListItem = (list: string[], setList: (v: string[]) => void, index: number) => {
    setList(list.filter((_, i) => i !== index));
  };

  const addDependency = () => {
    setDependencies([...dependencies, { role: '', description: '' }]);
  };

  const updateDependency = (index: number, field: keyof BusinessRoleDependencyInput, value: string) => {
    setDependencies(dependencies.map((d, i) => i === index ? { ...d, [field]: value } : d));
  };

  const removeDependency = (index: number) => {
    setDependencies(dependencies.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent size="lg" className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <DialogTitle>
              {isEdit ? t('businessRoles.editRole') : t('businessRoles.createRole')}
            </DialogTitle>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-6">
          {error && (
            <div className="bg-destructive/5 border border-destructive/20 text-destructive rounded-lg px-4 py-3 text-sm">{error}</div>
          )}

          {/* Role Information */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">{t('businessRoles.roleInformation')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('businessRoles.roleName')} *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="role-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('businessRoles.roleType')}</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as BusinessRoleType)}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="role-type-select"
                >
                  {Object.entries(roleTypeLabels).map(([val, label]) => (
                    <option key={val} value={val}>{t(`businessRoles.type${label}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('businessRoles.category')}</label>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="role-category-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('businessRoles.status')}</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as BusinessRoleStatus)}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="role-status-select"
                >
                  {Object.entries(statusLabels).map(([val, label]) => (
                    <option key={val} value={val}>{t(`businessRoles.status${label}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('businessRoles.numberOfSeats')}</label>
                <input
                  type="number"
                  min={1}
                  value={numberOfSeats}
                  onChange={(e) => setNumberOfSeats(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="role-seats-input"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-foreground mb-1">{t('businessRoles.description')}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="role-description-input"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">{t('businessRoles.tags')}</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm">
                  {tag}
                  <button onClick={() => removeListItem(tags, setTags, i)} className="text-primary hover:text-primary">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem(tags, setTags, newTag, setNewTag))}
                placeholder={t('businessRoles.addTag')}
                className="flex-1 border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
              <Button variant="secondary" size="sm" onClick={() => addListItem(tags, setTags, newTag, setNewTag)}>
                {t('businessRoles.add')}
              </Button>
            </div>
          </div>

          {/* Required Skills */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">{t('businessRoles.requiredSkills')}</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {requiredSkills.map((skill, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/5 text-primary rounded-md text-sm">
                  {skill}
                  <button onClick={() => removeListItem(requiredSkills, setRequiredSkills, i)} className="text-primary hover:text-primary">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem(requiredSkills, setRequiredSkills, newSkill, setNewSkill))}
                placeholder={t('businessRoles.addSkill')}
                className="flex-1 border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
              <Button variant="secondary" size="sm" onClick={() => addListItem(requiredSkills, setRequiredSkills, newSkill, setNewSkill)}>
                {t('businessRoles.add')}
              </Button>
            </div>
          </div>

          {/* Needed Equipment */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">{t('businessRoles.neededEquipment')}</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {neededEquipment.map((item, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/5 text-primary rounded-md text-sm">
                  {item}
                  <button type="button" onClick={() => setNeededEquipment(neededEquipment.filter((_, idx) => idx !== i))} className="text-primary hover:text-primary">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </span>
              ))}
            </div>
            {categories.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('businessRoles.neededEquipmentNoCategories')}</p>
            ) : (
              <select
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (v && !neededEquipment.includes(v)) setNeededEquipment([...neededEquipment, v]);
                }}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              >
                <option value="">{t('businessRoles.neededEquipmentPick')}</option>
                {categories.filter(c => !neededEquipment.includes(c.name)).map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Core Responsibilities */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">{t('businessRoles.coreResponsibilities')}</label>
            <div className="space-y-2 mb-2">
              {coreResponsibilities.map((resp, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm">{'\u2022'}</span>
                  <span className="flex-1 text-sm text-foreground">{resp}</span>
                  <button onClick={() => removeListItem(coreResponsibilities, setCoreResponsibilities, i)} className="text-muted-foreground hover:text-destructive">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newResponsibility}
                onChange={(e) => setNewResponsibility(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem(coreResponsibilities, setCoreResponsibilities, newResponsibility, setNewResponsibility))}
                placeholder={t('businessRoles.addResponsibility')}
                className="flex-1 border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
              <Button variant="secondary" size="sm" onClick={() => addListItem(coreResponsibilities, setCoreResponsibilities, newResponsibility, setNewResponsibility)}>
                {t('businessRoles.add')}
              </Button>
            </div>
          </div>

          {/* Decision Rights */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">{t('businessRoles.decisionRights')}</label>
            <div className="space-y-2 mb-2">
              {decisionRights.map((dr, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm">{'\u2022'}</span>
                  <span className="flex-1 text-sm text-foreground">{dr}</span>
                  <button onClick={() => removeListItem(decisionRights, setDecisionRights, i)} className="text-muted-foreground hover:text-destructive">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newDecisionRight}
                onChange={(e) => setNewDecisionRight(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem(decisionRights, setDecisionRights, newDecisionRight, setNewDecisionRight))}
                placeholder={t('businessRoles.addDecisionRight')}
                className="flex-1 border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
              <Button variant="secondary" size="sm" onClick={() => addListItem(decisionRights, setDecisionRights, newDecisionRight, setNewDecisionRight)}>
                {t('businessRoles.add')}
              </Button>
            </div>
          </div>

          {/* Dependencies */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">{t('businessRoles.dependencies')}</label>
            <div className="space-y-3 mb-2">
              {dependencies.map((dep, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3 bg-muted rounded-lg">
                  <input
                    type="text"
                    value={dep.role}
                    onChange={(e) => updateDependency(i, 'role', e.target.value)}
                    placeholder={t('businessRoles.dependencyRole')}
                    className="border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  />
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={dep.description}
                      onChange={(e) => updateDependency(i, 'description', e.target.value)}
                      placeholder={t('businessRoles.dependencyDescription')}
                      className="flex-1 border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    />
                    <button onClick={() => removeDependency(i)} className="text-muted-foreground hover:text-destructive">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="link" size="sm" onClick={addDependency} iconName="Plus">
              {t('businessRoles.addDependency')}
            </Button>
          </div>

          {/* Success Criteria */}
          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
            <label className="block text-sm font-semibold text-foreground mb-3">{t('businessRoles.successCriteria')}</label>
            <textarea
              value={successCriteria.description || ''}
              onChange={(e) => setSuccessCriteria({ ...successCriteria, description: e.target.value })}
              rows={3}
              placeholder={t('businessRoles.successCriteriaPlaceholder')}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary bg-card"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">{t('businessRoles.lastReviewDate')}</label>
                <input
                  type="date"
                  value={successCriteria.lastReviewDate?.split('T')[0] || ''}
                  onChange={(e) => setSuccessCriteria({ ...successCriteria, lastReviewDate: e.target.value || undefined })}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary bg-card"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">{t('businessRoles.nextReviewDate')}</label>
                <input
                  type="date"
                  value={successCriteria.nextReviewDate?.split('T')[0] || ''}
                  onChange={(e) => setSuccessCriteria({ ...successCriteria, nextReviewDate: e.target.value || undefined })}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary bg-card"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">{t('businessRoles.reviewedBy')}</label>
                <input
                  type="text"
                  value={successCriteria.reviewedBy || ''}
                  onChange={(e) => setSuccessCriteria({ ...successCriteria, reviewedBy: e.target.value || undefined })}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary bg-card"
                />
              </div>
            </div>
          </div>

          {/* Revision Comment (edit only) */}
          {isEdit && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('businessRoles.revisionComment')}</label>
              <input
                type="text"
                value={revisionComment}
                onChange={(e) => setRevisionComment(e.target.value)}
                placeholder={t('businessRoles.revisionCommentPlaceholder')}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('businessRoles.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            loading={saving}
            data-testid="save-role-button"
          >
            {isEdit ? t('businessRoles.saveChanges') : t('businessRoles.createRole')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
